import type { ClipboardEvent } from 'react'
import { useDropzone } from 'react-dropzone'
import { cn } from '@joyful/utils'

import { Button, Stack, toast } from '../../../../core'
import type { UserFeedbackDialogController } from '../hooks/useUserFeedbackDialog'

const MaxScreenshotFileSizeBytes = 10 * 1024 * 1024
const PastedScreenshotFileName = 'pasted-screenshot.png'
const PngFileSignatureBytes = new Uint8Array([
  0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a,
])
const ScreenshotRejectedToastMessage = `Screenshots must be PNG images under 10\u00A0MB.`

export interface ScreenshotAttachmentFieldProps {
  isDisabled: boolean
  screenshotDialog: UserFeedbackDialogController
}

export function ScreenshotAttachmentField({
  isDisabled,
  screenshotDialog,
}: ScreenshotAttachmentFieldProps) {
  const screenshotDropzone = useDropzone({
    accept: { 'image/png': ['.png'] },
    disabled: isDisabled,
    maxFiles: 1,
    maxSize: MaxScreenshotFileSizeBytes,
    multiple: false,
    noPaste: true,
    onDrop: (acceptedFiles, fileRejections) => {
      const screenshotFile = acceptedFiles.at(0)
      if (screenshotFile !== undefined) {
        void acceptScreenshotFile({
          isDisabled,
          screenshotDialog,
          screenshotFile,
        })
      }

      if (fileRejections.length > 0) {
        toast.error(ScreenshotRejectedToastMessage)
      }
    },
  })

  return (
    <Stack gap="sm">
      <Stack direction="horizontal" align="center" justify="between" gap="sm">
        <p className="text-tremor-content-emphasis text-sm font-medium">
          {`Screenshot`}
        </p>
        {screenshotDialog.screenshot !== null ? (
          <Button
            colorVariant="destructive"
            type="button"
            size="xs"
            variant="ghost"
            disabled={isDisabled}
            onClick={screenshotDialog.clearScreenshot}
          >
            {`Remove screenshot`}
          </Button>
        ) : null}
      </Stack>
      {screenshotDialog.screenshot !== null ? (
        <img
          src={screenshotDialog.screenshot.objectUrl}
          alt={`Screenshot preview`}
          className="mx-auto max-h-64 max-w-full rounded-lg border object-contain"
        />
      ) : (
        <Stack asChild align="center" justify="center" gap="xs">
          <div
            {...screenshotDropzone.getRootProps({
              className: cn(
                'border-tremor-border bg-tremor-background-subtle text-tremor-content h-48 cursor-pointer rounded-md border border-dashed text-sm',
                screenshotDropzone.isDragActive &&
                  'border-tremor-brand bg-tremor-brand-faint text-tremor-brand-emphasis',
              ),
            })}
          >
            <input {...screenshotDropzone.getInputProps()} />
            {screenshotDropzone.isDragActive ? (
              <p className="font-medium">{`Drop your screenshot here`}</p>
            ) : (
              <>
                <p className="font-medium">{`No screenshot attached`}</p>
                <p className="text-tremor-content-subtle text-xs">
                  {`Drag and drop a PNG, click to upload, or paste`}
                </p>
              </>
            )}
          </div>
        </Stack>
      )}
    </Stack>
  )
}

export function handleScreenshotAttachmentPaste({
  event,
  isDisabled,
  screenshotDialog,
}: {
  event: ClipboardEvent
  isDisabled: boolean
  screenshotDialog: UserFeedbackDialogController
}) {
  const pastedScreenshotFile = getPastedScreenshotFile(event.clipboardData)
  if (pastedScreenshotFile === null) {
    return
  }

  event.preventDefault()
  void acceptScreenshotFile({
    isDisabled,
    screenshotDialog,
    screenshotFile: pastedScreenshotFile,
  })
}

async function acceptScreenshotFile({
  isDisabled,
  screenshotDialog,
  screenshotFile,
}: {
  isDisabled: boolean
  screenshotDialog: UserFeedbackDialogController
  screenshotFile: File
}) {
  if (isDisabled) {
    return false
  }

  if (!isWithinScreenshotSizeLimit(screenshotFile)) {
    toast.error(ScreenshotRejectedToastMessage)
    return false
  }

  const hasPngSignature = await hasPngFileSignature(screenshotFile)
  if (!hasPngSignature) {
    toast.error(ScreenshotRejectedToastMessage)
    return false
  }

  const normalizedScreenshotFile = normalizePastedScreenshotFile(screenshotFile)
  screenshotDialog.setScreenshotFromFile(normalizedScreenshotFile)
  return true
}

function getPastedScreenshotFile(clipboardData: DataTransfer | null) {
  if (clipboardData === null) {
    return null
  }

  const clipboardFile = Array.from(clipboardData.files).find((file) => {
    return file.type.startsWith('image/')
  })
  if (clipboardFile !== undefined) {
    return clipboardFile
  }

  for (const clipboardItem of Array.from(clipboardData.items)) {
    if (
      clipboardItem.kind === 'file' &&
      clipboardItem.type.startsWith('image/')
    ) {
      const itemFile = clipboardItem.getAsFile()
      if (itemFile !== null) {
        return itemFile
      }
    }
  }

  return null
}

function isWithinScreenshotSizeLimit(screenshotFile: File) {
  return screenshotFile.size <= MaxScreenshotFileSizeBytes
}

async function hasPngFileSignature(screenshotFile: File) {
  const signatureBytes = new Uint8Array(
    await screenshotFile.slice(0, PngFileSignatureBytes.length).arrayBuffer(),
  )

  if (signatureBytes.length < PngFileSignatureBytes.length) {
    return false
  }

  return PngFileSignatureBytes.every((byte, index) => {
    return signatureBytes[index] === byte
  })
}

function normalizePastedScreenshotFile(screenshotFile: File) {
  if (
    screenshotFile.type === 'image/png' &&
    screenshotFile.name.toLowerCase().endsWith('.png')
  ) {
    return screenshotFile
  }

  return new File([screenshotFile], PastedScreenshotFileName, {
    type: 'image/png',
  })
}
